/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  EventEmitter,
  useSubscription,
  StatefulEmitter,
  TLabel,
} from '@apia/util';
import { FC } from 'react';
import { TOption } from '../components';
import {
  TFocusRetriever,
  TValidationFunction,
  TValidationRules,
} from '../types';
import { Box } from '@apia/theme';
import { Collector } from './Collector';

export abstract class CollectorField<
  ValueType = any,
  FieldProps = any,
> extends EventEmitter<{
  focusFunctionUpdate: TFocusRetriever;
  functionUpdate: TValidationFunction<ValueType>;
  optionsUpdate: TOption[];
  propsUpdate: Partial<FieldProps>;
  rulesUpdate: TValidationRules;
}> {
  /**
   * Este es el componente de implementación de cada campo particular
   */
  abstract Component: FC;
  abstract type: string;

  #emitter = new StatefulEmitter<{
    isHidden: boolean;
  }>({
    isHidden: false,
  });

  constructor(
    public readonly name: string,
    protected label: string | TLabel,
    properties?: Partial<FieldProps>,
  ) {
    super();

    if (properties) {
      this.properties = properties;
    }
  }

  protected _collector: Collector = {} as Collector;

  public get collector() {
    return this._collector;
  }

  public set collector(c: Collector) {
    this._collector = c;
  }

  isHidden = () => this.#emitter.getState('isHidden');
  setIsHidden = (isHidden: boolean) => {
    this.#emitter.emit('isHidden', isHidden);
  };

  protected properties: Partial<FieldProps> = {};
  setProperties = (newProps: Partial<FieldProps>) => {
    this.properties = { ...this.properties, ...newProps };
    this.emit('propsUpdate', { ...this.properties });
  };

  protected useProperties = () => {
    return useSubscription<Partial<FieldProps>>({
      getInitialState: () => this.properties,
      makeSubscription: (setProps) => this.on('propsUpdate', setProps),
    });
  };

  /**
   * Este componente se utiliza para controlar propiedades comunes a todos los
   * campos.
   */
  SuperComponent = () => {
    if (this.#emitter.useState('isHidden')) return null;

    return (
      <Box className={`collector__field ${this.type}`}>
        <this.Component />
      </Box>
    );
  };
}
